import React, {
  useEffect,
  useState,
  Context,
  createContext,
  useRef,
  useReducer,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { Header } from '@everlywell/leaves';

import GlobalStyle from 'styles/global';
import { ariesTheme } from 'styles/ariesTheme';
import { ThemeContext, Theme } from 'components/Themable';
import { getUser } from 'utils/cartHelpers';
import ewAnalytics from 'utils/analytics';
import { STORE_ROOT, RESULTS_ROOT } from 'utils/constants/urls';
import image from '../../images/logo/everlywell-logo.svg';
import * as S from './styles';
import loadable from '@loadable/component';
import { Spinner } from 'styles/utils';
import { initialState, reducer } from '../../containers/CartContainer/store';
import { setShowTOS } from '../../containers/CartContainer/store/actions';

const Cart = loadable(() => import('../../containers/CartContainer'), {
  fallback: <Spinner />,
});

const Footer = loadable(() => import('../Footer'), {
  fallback: <Spinner />,
});

type Props = {
  children: React.ReactNode;
};

interface UserType {
  userId: number | null;
  loggedIn: boolean;
  isMember: boolean;
  membershipType: string | null;
  activeCredits: number | null;
  fetched: boolean;
}

interface CartContextType {
  setTOS: Function;
}

const defaultUser = {
  userId: null,
  loggedIn: false,
  isMember: false,
  membershipType: null,
  activeCredits: null,
  fetched: false,
};

export const UserContext: Context<UserType> = createContext<UserType>(
  defaultUser,
);

export const CartContext: Context<CartContextType> = createContext<
  CartContextType
>({ setTOS: () => {} });

const accountLinks = {
  label: 'Account',
  results: {
    label: 'Test Results',
    url: `${RESULTS_ROOT}/dashboard`,
  },
  settings: {
    label: 'Settings',
    url: `${RESULTS_ROOT}/account/settings`,
  },
  logout: {
    label: 'Log Out',
    url: `${STORE_ROOT}/logout`,
  },
};

const Layout = (props: Props) => {
  const [userData, setUserData] = useState(defaultUser);
  const [cartState, dispatch] = useReducer(reducer, initialState);
  const cartProps = { cartState, dispatch };
  const headerRef = useRef<HTMLAnchorElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    if (window) {
      /* Cart Global Variables */
      // @ts-ignore
      window.appRoot = '';
    }

    const runEffect = async () => {
      try {
        const data = await getUser();
        const sessionData = data ? data.data : {};
        const {
          logged_in: loggedIn,
          is_member: isMember,
          membership_type: membershipType,
          active_credits: activeCredits,
          user_id: userId,
        } = sessionData;

        setUserData({
          loggedIn,
          isMember,
          membershipType,
          activeCredits,
          userId,
          fetched: true,
        });
      } catch (e) {
        setUserData({
          ...defaultUser,
          fetched: true,
        });
      }
    };
    runEffect();
  }, []);

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const handleTrackableLinkClick = (e: Event) => {
    ewAnalytics.eventHandler(e);
  };

  // links defined in the Header have data-* attributes that get picked up in order to track via Mixpanel
  // the links aren't mounted when the header is closed, so we listen for the header drawer to open, and attach/remove listeners appropriately
  useEffect(() => {
    let trackableLinks: NodeListOf<HTMLAnchorElement> | undefined;

    if (menuOpen || subMenuOpen) {
      trackableLinks = headerRef?.current?.querySelectorAll(
        '[data-analytics-event]',
      );
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.addEventListener('click', handleTrackableLinkClick),
        );
      }
    }
    return () => {
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.removeEventListener('click', handleTrackableLinkClick),
        );
      }
    };
  }, [menuOpen, subMenuOpen]);

  return (
    <div id="app" className="siteRoot">
      <GlobalStyle />
      <ThemeProvider theme={ariesTheme}>
        <ThemeContext.Provider value={Theme.Default}>
          <UserContext.Provider value={userData}>
            {userData.fetched ? (
              <Header
                loggedIn={userData.loggedIn}
                logoSrc={image}
                logoAlt="Everlywell"
                id="everlywellHeader"
                ref={headerRef}
                cartComponent={<Cart {...cartProps} />}
                accountLinks={accountLinks}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                onSubMenuOpen={() => setSubMenuOpen(true)}
                onSubMenuClose={() => setSubMenuOpen(false)}
              />
            ) : null}
            <S.Layout>
              <CartContext.Provider value={{ setTOS }}>
                {props.children}
              </CartContext.Provider>
            </S.Layout>
            <Footer />
          </UserContext.Provider>
        </ThemeContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export default Layout;
